import { useEffect, useRef, useState } from 'react';
import Popover from '@mui/material/Popover';
import { useAppState } from '../../app/state';
import { apiRequest } from '../../api/client';
import { canAssignDoc } from '../../lib/responsibility';
import { DocResponsiblesPicker } from '../../components/DocResponsiblesPicker';
import { WorkflowControl } from './WorkflowControl';
import type { DocId } from '../../lib/types';

/**
 * The doc bar's OWNERSHIP CLUSTER (V0.42, Option A — the validated POC).
 *
 * Responsibles and the workflow state, boxed together as ONE workflow unit and read LEFT TO
 * RIGHT: who owns this language's version, where it stands. The box (its border and faint
 * WORKFLOW tag) sets the two apart from the bar's other controls.
 *
 * WHO SEES IT: the caller draws the cluster for documentation participants, and since V0.44.1
 * only while the map's Edit toggle is on — it is the working view. Within it, the responsibles
 * are EDITABLE for `mayAssignDoc` (an administrator or a branch owner) and READ-ONLY names for
 * every other participant — ownership is a visible fact, not an admin secret. The workflow
 * chip's own verbs gate themselves.
 *
 * THE LANGUAGE IS THE BAR'S, NOT THE BOX'S (V0.44.1). The box used to open with its own
 * selector; the bar now carries the ONE language selector the map's top bar has, always
 * visible, and this box follows it: the responsibles shown are those of the language being
 * READ (`selLocale`, re-seeded from the page's resolved locale), which is also what V0.43.2
 * made the old selector mean. Choosing French while reading the English fallback and assigning
 * somebody is still how "please write the French one" is said (V0.42 inverts V0.38's fallback
 * withholding for assignment alone). `GET /api/docs/:id/responsibles` carries every locale's
 * assignments, so a language switch shows who owns THAT language with no refetch. The workflow
 * chip stays about the VIEWED page — its state is the page on screen.
 *
 * The responsibles moved OUT of the workflow popover into this box (one implementation): the
 * picker's dropdown overlays the page, which for an act this occasional is the right trade.
 */

type LocaleAssignees = { responsibles: string[]; responsibleGroups: string[] };
type ByLocale = Record<string, LocaleAssignees>;

const EMPTY: LocaleAssignees = { responsibles: [], responsibleGroups: [] };

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function DocOwnershipCluster({
  docId,
  seed,
}: {
  docId: DocId;
  seed?: { id: string; locale?: string; workflowState?: string; reviewedAt?: string | null; reviewedByName?: string | null } | null;
}) {
  const s = useAppState();
  const entry = s.docWorkflowLoaded ? s.docWorkflow[docId] : undefined;
  const resolved = entry?.locale ?? s.currentLocale;

  const [selLocale, setSelLocale] = useState(resolved);
  const [byLocale, setByLocale] = useState<ByLocale>({});
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLButtonElement>(null);

  // The assignment language follows the page's resolved locale when the page (or its
  // resolution) changes — never mid-edit of the selector.
  useEffect(() => {
    setSelLocale(resolved);
  }, [docId, resolved]);

  // Every locale's assignments for this page, once per open. `docOpenSeq` re-asks when the
  // same page is reopened (a restore can change nothing here, but the pattern matches the chip).
  useEffect(() => {
    let live = true;
    setByLocale({});
    apiRequest<{ byLocale: ByLocale }>(`/api/docs/${docId}/responsibles`).then(
      (r) => {
        if (live) setByLocale(r.byLocale || {});
      },
      () => {
        if (live) setByLocale({});
      },
    );
    return () => {
      live = false;
    };
  }, [docId, s.docOpenSeq]);

  const cur = byLocale[selLocale] ?? EMPTY;
  const mayAssign = canAssignDoc(s, docId);
  const directoriesReady = s.userDirectoryLoaded && s.groupDirectoryLoaded;
  const nameOf = (id: string) => s.userDirectory.find((u) => u.id === id)?.displayName ?? id;
  const groupNameOf = (id: string) => s.groupDirectory.find((g) => g.id === id)?.name ?? id;
  const count = cur.responsibles.length + cur.responsibleGroups.length;

  // Optimistic: the picker's chosen users/groups become the box summary immediately, and the
  // PUT expands groups server-side (the box shows chosen users + group chips, which is honest).
  const applyLocal = (users: string[], groups: string[]) =>
    setByLocale((prev) => ({ ...prev, [selLocale]: { responsibles: users, responsibleGroups: groups } }));

  return (
    <div className="wf-cluster" id="doc-ownership">
      <span className="wf-cluster-tag" aria-hidden="true">
        WORKFLOW
      </span>

      {/* The language selector that opened this box until V0.44.1 is the bar's `#doc-lang` now
          (see the header); `selLocale` still names the language these responsibles own. */}

      {/* Responsibles — first. Opens its dropdown over the page. */}
      <button
        type="button"
        className="wf-respbox"
        id="wf-respbox"
        ref={boxRef}
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : 'false'}
        title={mayAssign ? 'Assign who is responsible for this language' : 'Who is responsible for this language'}
        onClick={() => setOpen(true)}
      >
        {count ? (
          <>
            <span className="wf-resp-stack">
              {cur.responsibles.slice(0, 3).map((id) => (
                <span className="wf-resp-av" key={id} title={nameOf(id)}>
                  {initialsOf(nameOf(id))}
                </span>
              ))}
              {cur.responsibleGroups.slice(0, 2).map((id) => (
                <span className="wf-resp-av grp" key={id} title={groupNameOf(id)}>
                  {initialsOf(groupNameOf(id))}
                </span>
              ))}
            </span>
            <span className="wf-resp-count">{count}</span>
          </>
        ) : (
          <span className="wf-resp-none">Unassigned</span>
        )}
        {mayAssign ? <span className="wf-caret" aria-hidden="true">▾</span> : null}
      </button>

      <span className="wf-div" aria-hidden="true" />

      {/* Workflow state — rightmost. The existing chip and its popover of verbs/history. */}
      <WorkflowControl key={docId} docId={docId} seed={seed} />

      <Popover
        open={open}
        anchorEl={boxRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className="wf-resp-panel">
          <div className="review-head">
            <span className="review-title">Responsibles</span>
            <span className="review-lang">{selLocale}</span>
          </div>
          {mayAssign && directoriesReady ? (
            <DocResponsiblesPicker
              // Reseed when the page or the language changes — a different (doc, locale)
              // resolves different assignments, and the drafts must not carry the old ones.
              key={`${docId}:${selLocale}`}
              docId={docId}
              locale={selLocale}
              s={s}
              seedUsers={cur.responsibles}
              seedGroups={cur.responsibleGroups}
              onChange={applyLocal}
            />
          ) : (
            <div className="review-empty" id="wf-responsibles-read">
              {count
                ? [...cur.responsibles.map(nameOf), ...cur.responsibleGroups.map(groupNameOf)].join(', ')
                : 'Nobody yet.'}
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

/* The editable responsibles picker that lived here moved to components/DocResponsiblesPicker.tsx
   (V0.44) — the dashboard's Documents tab assigns the same thing for the same language. */
